import { useCallback, useEffect, useState } from 'react'
import { useHubAuth } from '@/context/HubAuthContext'
import { supabase } from '@/lib/supabase'
import {
  DEFAULT_HERO_CONFIG,
  HERO_BG_ANIMATIONS,
  HERO_OVERLAYS,
  HERO_TEXT_ANIMATIONS,
  HERO_TEXT_EFFECTS,
  heroFormToJson,
  mergeHeroFromRpc,
} from '@/lib/siteHeroConfig'
import { cn } from '@/lib/utils'

export default function HubSiteSettingsPage() {
  const { user } = useHubAuth()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  /** When false, the public RPC returns nothing — the homepage keeps built-in defaults. Default on so a first save actually goes live. */
  const [published, setPublished] = useState(true)
  const [error, setError] = useState('')
  const [toast, setToast] = useState(null)
  const [form, setForm] = useState(() => ({
    phrases: [...DEFAULT_HERO_CONFIG.phrases.map((p) => ({ ...p }))],
    rotate_seconds: DEFAULT_HERO_CONFIG.rotate_seconds,
    shuffle: DEFAULT_HERO_CONFIG.shuffle,
    text_animation: DEFAULT_HERO_CONFIG.text_animation,
    background_animation: DEFAULT_HERO_CONFIG.background_animation,
    background_image_url: DEFAULT_HERO_CONFIG.background_image_url || '',
    overlay: DEFAULT_HERO_CONFIG.overlay,
    text_effect: DEFAULT_HERO_CONFIG.text_effect,
  }))

  const load = useCallback(async () => {
    if (!user?.id) {
      setLoading(false)
      return
    }
    setLoading(true)
    setError('')
    const { data, error: qErr } = await supabase
      .from('hub_site_settings')
      .select('hero,published_for_public_site')
      .eq('user_id', user.id)
      .maybeSingle()

    setLoading(false)
    if (qErr) {
      setError(qErr.message)
      return
    }
    if (!data) {
      setPublished(true)
      return
    }
    setPublished(Boolean(data.published_for_public_site))
    const merged = mergeHeroFromRpc(data.hero ?? {})
    setForm({
      phrases: merged.phrases.map((p) => ({ line1: p.line1, line2: p.line2 })),
      rotate_seconds: merged.rotate_seconds,
      shuffle: merged.shuffle,
      text_animation: merged.text_animation,
      background_animation: merged.background_animation,
      background_image_url: merged.background_image_url || '',
      overlay: merged.overlay,
      text_effect: merged.text_effect,
    })
  }, [user?.id])

  useEffect(() => {
    load()
  }, [load])

  const setPhrase = (i, field, val) => {
    setForm((f) => {
      const phrases = [...f.phrases]
      phrases[i] = { ...phrases[i], [field]: val }
      return { ...f, phrases }
    })
  }

  const addPhrase = () => {
    setForm((f) => ({
      ...f,
      phrases: [...f.phrases, { line1: '', line2: '' }],
    }))
  }

  const removePhrase = (i) => {
    setForm((f) => ({
      ...f,
      phrases: f.phrases.filter((_, j) => j !== i),
    }))
  }

  const handleSave = async () => {
    if (!user?.id) return
    setSaving(true)
    setError('')
    setToast(null)
    const hero = heroFormToJson({
      ...form,
      background_image_url: form.background_image_url || null,
    })
    try {
      const { error: upErr } = await supabase.from('hub_site_settings').upsert(
        {
          user_id: user.id,
          hero,
          published_for_public_site: published,
        },
        { onConflict: 'user_id' }
      )
      if (upErr) throw upErr
      setToast(
        published
          ? 'Saved. The public homepage will use this hero on the next visit.'
          : 'Saved as draft only. The public homepage still shows the default headline until you enable publish below.'
      )
      await load()
    } catch (e) {
      setError(e.message || String(e))
    } finally {
      setSaving(false)
    }
  }

  const fieldClass =
    'mt-1 w-full rounded-md border border-border bg-background px-2.5 py-1.5 text-sm text-foreground placeholder:text-muted-foreground'

  const labelClass = 'text-xs font-medium text-muted-foreground'

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-lg font-semibold tracking-tight">Site — hero</h1>
        <p className="mt-1 max-w-xl text-sm text-muted-foreground">
          Headlines and visuals for the public landing hero. The database migration must be applied, and{' '}
          <span className="font-medium text-foreground">Publish hero to public site</span> must stay on — otherwise
          the homepage keeps the built-in default copy (the RPC only reads a published row).
        </p>
      </div>

      {!user?.id ? (
        <p className="text-sm text-muted-foreground">Sign in to edit.</p>
      ) : loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <div className="max-w-2xl space-y-8">
          {error ? (
            <div className="rounded-md border border-destructive/50 bg-card px-3 py-2 text-sm text-destructive">{error}</div>
          ) : null}
          {toast ? <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-sm">{toast}</div> : null}

          <div className="rounded-lg border border-border bg-card p-4">
            <h2 className="text-sm font-semibold tracking-tight">Phrases</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              Each phrase has line 1 and line 2 (optional). Remove all phrases to hide the headline on the homepage (or keep a
              background image only). Multiple phrases rotate; optionally shuffle order.
            </p>
            <div className="mt-4 space-y-4">
              {form.phrases.length === 0 ? (
                <p className="rounded-md border border-dashed border-border bg-background/40 px-3 py-2 text-sm text-muted-foreground">
                  No phrases — no hero headline on the public site. Add one below, or set a background image URL to show a
                  full-bleed panel without text.
                </p>
              ) : null}
              {form.phrases.map((p, i) => (
                <div key={i} className="rounded-md border border-border/80 bg-background/60 p-3">
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-[11px] font-mono uppercase text-muted-foreground">Phrase {i + 1}</span>
                    <button
                      type="button"
                      onClick={() => removePhrase(i)}
                      className="text-[11px] text-muted-foreground hover:text-foreground"
                    >
                      Remove
                    </button>
                  </div>
                  <label className={cn(labelClass, 'mt-3 block')} htmlFor={`h-l1-${i}`}>
                    Line 1
                  </label>
                  <input
                    id={`h-l1-${i}`}
                    className={fieldClass}
                    value={p.line1}
                    onChange={(e) => setPhrase(i, 'line1', e.target.value)}
                    placeholder="First line…"
                  />
                  <label className={cn(labelClass, 'mt-3 block')} htmlFor={`h-l2-${i}`}>
                    Line 2
                  </label>
                  <input
                    id={`h-l2-${i}`}
                    className={fieldClass}
                    value={p.line2}
                    onChange={(e) => setPhrase(i, 'line2', e.target.value)}
                    placeholder="Second line… (optional)"
                  />
                </div>
              ))}
            </div>
            <button type="button" className="mt-3 text-xs font-medium text-primary hover:underline" onClick={addPhrase}>
              + Add phrase
            </button>

            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <div>
                <label className={labelClass} htmlFor="rotate_seconds">
                  Rotate interval (seconds)
                </label>
                <input
                  id="rotate_seconds"
                  type="number"
                  min={3}
                  max={120}
                  className={fieldClass}
                  value={form.rotate_seconds}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      rotate_seconds: Math.min(120, Math.max(3, Number(e.target.value) || f.rotate_seconds)),
                    }))
                  }
                />
              </div>
              <div className="flex items-end gap-2 pb-0.5">
                <label className="flex cursor-pointer items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.shuffle}
                    onChange={(e) => setForm((f) => ({ ...f, shuffle: e.target.checked }))}
                    className="rounded border-border"
                  />
                  Shuffle phrases (avoid repeating the same phrase when possible)
                </label>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-border bg-card p-4">
            <h2 className="text-sm font-semibold tracking-tight">Motion & overlays</h2>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div>
                <label className={labelClass} htmlFor="text_animation">
                  Text animation
                </label>
                <select
                  id="text_animation"
                  className={fieldClass}
                  value={form.text_animation}
                  onChange={(e) => setForm((f) => ({ ...f, text_animation: e.target.value }))}
                >
                  {HERO_TEXT_ANIMATIONS.map(([v, lbl]) => (
                    <option key={v} value={v}>
                      {lbl}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass} htmlFor="background_animation">
                  Background animation
                </label>
                <select
                  id="background_animation"
                  className={fieldClass}
                  value={form.background_animation}
                  onChange={(e) => setForm((f) => ({ ...f, background_animation: e.target.value }))}
                >
                  {HERO_BG_ANIMATIONS.map(([v, lbl]) => (
                    <option key={v} value={v}>
                      {lbl}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass} htmlFor="overlay">
                  Overlay / wash
                </label>
                <select
                  id="overlay"
                  className={fieldClass}
                  value={form.overlay}
                  onChange={(e) => setForm((f) => ({ ...f, overlay: e.target.value }))}
                >
                  {HERO_OVERLAYS.map(([v, lbl]) => (
                    <option key={v} value={v}>
                      {lbl}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass} htmlFor="text_effect">
                  Text styling
                </label>
                <select
                  id="text_effect"
                  className={fieldClass}
                  value={form.text_effect}
                  onChange={(e) => setForm((f) => ({ ...f, text_effect: e.target.value }))}
                >
                  {HERO_TEXT_EFFECTS.map(([v, lbl]) => (
                    <option key={v} value={v}>
                      {lbl}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="mt-4">
              <label className={labelClass} htmlFor="bg_url">
                Background image URL (HTTPS only, optional)
              </label>
              <input
                id="bg_url"
                className={fieldClass}
                value={form.background_image_url || ''}
                onChange={(e) => setForm((f) => ({ ...f, background_image_url: e.target.value }))}
                placeholder="https://…"
              />
            </div>
          </div>

          {!published ? (
            <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-foreground">
              <strong className="font-medium">Draft mode.</strong> Visitors still see the old default headline because{' '}
              <code className="font-mono text-xs">get_public_site_hero</code> only returns your JSON when publishing is on.
            </div>
          ) : null}

          <label className="flex cursor-pointer items-start gap-2 rounded-lg border border-dashed border-border bg-card px-4 py-3">
            <input
              type="checkbox"
              checked={published}
              onChange={(e) => setPublished(e.target.checked)}
              className="mt-0.5 rounded border-border"
            />
            <span>
              <span className="text-sm font-medium">Publish hero to public site</span>
              <span className="mt-0.5 block text-xs text-muted-foreground">
                Required for the live site. Only one published profile is allowed at a time (other accounts are
                unpublished automatically).
              </span>
            </span>
          </label>

          <button
            type="button"
            disabled={saving}
            onClick={handleSave}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-95 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save hero'}
          </button>

        </div>
      )}
    </div>
  )
}
